import { databases } from "@/lib/appwrite";
import { Query } from "appwrite";
import {
    type BaseTravelDocument,
    type BaseFetchTravelsResult,
    type BaseGlobalStats,
    type BaseUserStats,
} from "@/lib/collections/requestsTravels/types";

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const TRAVELS_COLLECTION_ID = import.meta.env.VITE_TRAVELS_REQUESTS_COLLECTION_ID;

export type TravelDocument = BaseTravelDocument;
export type FetchTravelsResult = BaseFetchTravelsResult;
export type GlobalStats = BaseGlobalStats;
export type UserStats = BaseUserStats;

export class AdminTravelsService {

    //  Obtiene todos los viajes (para admin/RRHH)

    static async getAllTravels(
        filters: {
            userId?: string;
            month?: string;
            searchQuery?: string;
            statusFilter?: string;
            allowedUserIds?: string[];
        },
        limit: number = 10,
        offset: number = 0
    ): Promise<FetchTravelsResult> {
        try {
            const queries = [
                Query.orderDesc("$createdAt"),
                Query.limit(1000),
            ];

            if (filters.userId && filters.userId !== "all") {
                queries.push(Query.equal("userId", filters.userId));
            }

            if (filters.statusFilter && filters.statusFilter !== "all") {
                queries.push(Query.equal("statusByRrhh", filters.statusFilter));
            }

            const allTravels = await databases.listDocuments(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                queries,
            );

            let filteredDocs = allTravels.documents as unknown as TravelDocument[];

            // Filtro por subordinados (supervisor)
            if (filters.allowedUserIds && filters.allowedUserIds.length > 0) {
                filteredDocs = filteredDocs.filter(doc =>
                    doc.usersId.some(id => filters.allowedUserIds!.includes(id))
                );
            }

            // Filtro por mes
            if (filters.month && filters.month !== 'all' && !filters.searchQuery?.trim()) {
                filteredDocs = filteredDocs.filter(doc => {
                    const docDate = new Date(doc.startDate);
                    const docMonth = `${docDate.getFullYear()}-${String(docDate.getMonth() + 1).padStart(2, '0')}`;
                    return docMonth === filters.month;
                });
            }

            // Filtro por búsqueda
            if (filters.searchQuery && filters.searchQuery.trim()) {
                const query = filters.searchQuery.trim();

                filteredDocs = filteredDocs.filter(doc => {
                    // Match exacto por ID si el query parece un ID de Appwrite
                    if (doc.$id === query) {
                        return true;
                    }

                    // Búsqueda parcial en campos de texto
                    const lowerQuery = query.toLowerCase();
                    return (
                        doc.title?.toLowerCase().includes(lowerQuery) ||
                        doc.tripPurpose?.toLowerCase().includes(lowerQuery) ||
                        doc.address?.toLowerCase().includes(lowerQuery) ||
                        doc.provinces?.some(p => p.toLowerCase().includes(lowerQuery)) ||
                        doc.countries?.some(c => c.toLowerCase().includes(lowerQuery))
                    );
                });
            }
            const total = filteredDocs.length;
            const paginatedDocs = filteredDocs.slice(offset, offset + limit);

            return {
                documents: paginatedDocs,
                total: total
            };

        } catch (error) {
            throw new Error("No se pudieron cargar los viajes");
        }
    }


    //  Obtiene IDs de usuarios que tienen viajes

    static async getUsersWithTravels(): Promise<string[]> {
        try {
            const allTravels = await databases.listDocuments(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                [Query.limit(1000)]
            );

            const userIds = new Set<string>();
            allTravels.documents.forEach(doc => {
                userIds.add(doc.userId as string);
            });

            return Array.from(userIds);
        } catch (error) {
            console.error("Error fetching users with travels:", error);
            throw new Error("No se pudieron cargar los usuarios");
        }
    }

    //  Estadísticas globales

    static async getGlobalStats(monthFilter?: string): Promise<GlobalStats> {
        try {
            const allTravels = await databases.listDocuments(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                [Query.limit(1000)]
            );

            let docs = allTravels.documents as unknown as TravelDocument[];

            // Filtrar por mes si se proporciona
            if (monthFilter) {
                docs = docs.filter(doc => {
                    const docDate = new Date(doc.startDate);
                    const docMonth = `${docDate.getFullYear()}-${String(docDate.getMonth() + 1).padStart(2, '0')}`;
                    return docMonth === monthFilter;
                });
            }

            const userIds = new Set<string>();
            docs.forEach(doc => userIds.add(doc.userId));

            return {
                totalRecords: docs.length,
                totalUsers: userIds.size,
                pending: docs.filter(d => d.statusByRrhh === 'pending').length,
                approved: docs.filter(d => d.statusByRrhh === 'approved').length,
                rejected: docs.filter(d => d.statusByRrhh === 'rejected').length
            };
        } catch (error) {
            console.error("Error fetching global stats:", error);
            throw new Error("No se pudieron cargar las estadísticas");
        }
    }


    //  Estadísticas por usuario

    static async getUserStats(userId: string, monthFilter?: string): Promise<UserStats> {
        try {
            const queries = [
                Query.equal("userId", userId),
                Query.limit(1000)
            ];

            const userTravels = await databases.listDocuments(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                queries
            );

            let docs = userTravels.documents as unknown as TravelDocument[];

            // Filtrar por mes si se proporciona
            if (monthFilter) {
                docs = docs.filter(doc => {
                    const docDate = new Date(doc.startDate);
                    const docMonth = `${docDate.getFullYear()}-${String(docDate.getMonth() + 1).padStart(2, '0')}`;
                    return docMonth === monthFilter;
                });
            }

            return {
                totalRecords: docs.length,
                pending: docs.filter(d => d.statusByRrhh === 'pending').length,
                approved: docs.filter(d => d.statusByRrhh === 'approved').length,
                rejected: docs.filter(d => d.statusByRrhh === 'rejected').length
            };
        } catch (error) {
            console.error("Error fetching user stats:", error);
            throw new Error("No se pudieron cargar las estadísticas del usuario");
        }
    }

    // Obtiene un viaje específico por ID
    static async getTravelById(travelId: string): Promise<TravelDocument> {
        try {
            const document = await databases.getDocument(
                DATABASE_ID,
                TRAVELS_COLLECTION_ID,
                travelId
            );
            return document as unknown as TravelDocument;
        } catch (error) {
            console.error("Error fetching travel by ID:", error);
            throw new Error("No se pudo cargar el viaje");
        }
    }
}


